import {
  hashKey,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import { isNotFound } from '@/common/error';

import { fetchTaskById, fetchTasks } from './tasks.api';
import type { TasksFilter, TasksListResponse } from './tasks.dto';

/**
 * A tasks list, cached under the filter that produced it.
 *
 * This is the whole point of the react-query layer here. The `tasksList` slice of the
 * zustand store is a single list shared by the calendar, the dashboard widgets, the
 * task pickers and the task pages, and every one of them writes it — so a page whose
 * filter is server-side (assignee, status, priority) would show whichever fetch landed
 * last rather than its own. Redirect into the tasks page fast enough and an unfiltered
 * fetch left behind by the page you came from resolves *after* the page's own and
 * replaces the list a second later: the filter is still applied, but the rows are not
 * the filter's rows.
 *
 * Keying the cache by the filter makes that impossible to express: a response can only
 * ever be read back under the exact filter that asked for it, and react-query drops the
 * result of a query whose key is no longer being observed.
 */

/** `filter` minus its page — the shape a *list* is identified by. */
export type TasksListFilter = Omit<TasksFilter, 'page'>;

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  /**
   * react-query hashes the filter deterministically, so key order never matters. The
   * filter and the page number are separate segments on purpose: paging within one
   * filter has to be told apart from switching filters (see `useTasksListQuery`).
   */
  list: (filter: TasksListFilter, page: number) => [...taskKeys.lists(), filter, page] as const,
  infinite: (filter: TasksListFilter) => [...taskKeys.lists(), 'infinite', filter] as const,
  /**
   * One task, as the task card reads it. Under `all` on purpose: every task write in the
   * store already invalidates `all`, so an open card refetches the task it changed.
   */
  detail: (id: string) => [...taskKeys.all, 'detail', id] as const,
};

/** Where the filter sits in a `taskKeys.list` key. */
const LIST_KEY_FILTER_INDEX = 2;

/**
 * One page of tasks — `limit` rows starting at `page`, with `count` saying how many
 * match the filter in total, which is what a page footer needs.
 *
 * The previous rows are held on screen while the next *page* loads, and dropped the
 * moment the *filter* changes. Those two cases look identical to a blanket
 * `keepPreviousData`, but they are opposites: paging is the same question asked
 * further down the list, so a flash of empty table between clicks is just noise;
 * a new filter is a different question, and showing the old answer under it is the
 * exact thing that reads as "the filter is broken".
 */
export const useTasksListQuery = (
  filter: TasksListFilter,
  page: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: taskKeys.list(filter, page),
    queryFn: () => fetchTasks({ ...filter, page }),
    // A caller whose `limit` is measured from the viewport has nothing worth asking for
    // until the measurement lands: fetching first would spend a request on a guessed
    // page size and throw the answer away a frame later.
    enabled: options?.enabled ?? true,

    placeholderData: (previousData, previousQuery) => {
      const previousFilter = previousQuery?.queryKey[LIST_KEY_FILTER_INDEX];
      const sameFilter = hashKey([previousFilter]) === hashKey([filter]);
      return sameFilter ? previousData : undefined;
    },
  });

/**
 * The same list, paged, for the surfaces that scroll into it.
 *
 * A page shorter than `limit` is the last one — activity answers a `count`, but it
 * counts what matches the filter before pagination, so the short page is the cheaper
 * and more reliable signal.
 */
export const useTasksInfiniteQuery = (filter: TasksListFilter, options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: taskKeys.infinite(filter),
    queryFn: ({ pageParam }) => fetchTasks({ ...filter, page: pageParam }),
    enabled: options?.enabled ?? true,
    initialPageParam: 1,
    getNextPageParam: (lastPage: TasksListResponse, allPages) =>
      lastPage.items.length < filter.limit ? undefined : allPages.length + 1,
  });

/**
 * Several tasks by id, each under the `taskKeys.detail` key the task card reads, so a task
 * one surface has loaded opens from cache on the other. A deleted task is a 404 that no retry
 * brings back, so it errors at once rather than after the backoff.
 */
export const useTasksByIdsQueries = (ids: string[]) =>
  useQueries({
    queries: ids.map((id) => ({
      queryKey: taskKeys.detail(id),
      queryFn: () => fetchTaskById(id),
      retry: (failureCount: number, error: Error) =>
        !isNotFound(error) && navigator.onLine && failureCount < 3,
    })),
  });

/**
 * Marks every cached tasks list stale, so the lists currently on screen refetch.
 *
 * Task writes still go through the zustand store — it owns the optimistic updates the
 * calendar and the widgets read — so a mutation is not visible to a query on its own.
 * Anything that creates, edits, deletes or re-statuses a task from a surface backed by
 * these hooks calls this afterwards.
 */
export const useInvalidateTasks = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: taskKeys.all });
  }, [queryClient]);
};
